<script setup lang="ts">
import { ref, watch } from 'vue';
import AutoComplete, { AutoCompleteCompleteEvent } from 'primevue/autocomplete';
import { useContacts, useStringFunctions } from '@apparatix/composables';
import type { AssociationContact, ContactSearchResult } from '@apparatix/types/Leads';
import { ContactCard } from './ContactCard';
import { PendingContact, searchResultTransformer } from '@apparatix/schemas/Contact';
import { WizardNewContact } from '@apparatix/schemas/CreateLeadWizard';

type Props = {
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
}

const props = defineProps<Props>();

const { searchContacts } = useContacts();
const { isEmptyString } = useStringFunctions();

const model = defineModel<PendingContact | WizardNewContact>();

const userInput = ref<string | AssociationContact>();
const suggestions = ref<ContactSearchResult[]>([]);

watch(() => userInput.value, () => {
  if (typeof userInput.value === 'object') {
    try {
      model.value = searchResultTransformer.parse(userInput.value);
    } catch (e) {
      model.value = undefined;
      if (import.meta.env.DEV) {
        throw e;
      }
    }
  }

  if (typeof userInput.value === 'string' && !isEmptyString(userInput.value, true)) {
    model.value = {
      fullName: userInput.value,
    };
  }

  if (!userInput.value || (typeof userInput.value === 'string' && isEmptyString(userInput.value, true))) {
    model.value = undefined;
  }
});

const autoComplete = ref(null);

/**
 * PrimeVue's AutoComplete stops waiting for suggestions two frames after a search starts, so results from a
 * request that takes longer never open the overlay when there were no suggestions before. Open it ourselves.
 */
const showSuggestions = (results: ContactSearchResult[]) => {
  suggestions.value = results;

  if (results.length && autoComplete.value?.$el?.contains(document.activeElement)) {
    autoComplete.value.show();
  }
};

const search = (e: AutoCompleteCompleteEvent) => searchContacts(e.query, results => {
  if (results?.length) {
    showSuggestions(results);
  } else {
    suggestions.value = [];
  }
});

const reset = () => {
  userInput.value = undefined;
  suggestions.value = [];
};

defineExpose({ reset });
</script>
<template>
  <AutoComplete
    ref="autoComplete"
    v-model="userInput"
    option-label="full_name"
    :placeholder="props.placeholder"
    :delay="300"
    :min-length="3"
    :disabled="props.disabled"
    :show-empty-message="false"
    :invalid="props.invalid"
    :focus-on-hover="false"
    :suggestions="suggestions"
    fluid
    @complete="search"
    @update:model-value="val => userInput = val"
  >
    <template #option="slotProps">
      <ContactCard
        :id="slotProps.option.id"
        variant="search"
        :name="slotProps.option.full_name ?? `${slotProps.option.first_name} ${slotProps.option.last_name}`"
        :company="slotProps.option.company_name"
        :owner="slotProps.option.ownerName"
      />
    </template>
  </AutoComplete>
</template>
